/**
 * Reusable "SMS blast a training course" modal.
 *
 * Used from both:
 *   - Training Courses list (row-level "SMS text" button — no navigation)
 *   - Training Course detail page (action row button)
 *
 * UX rules:
 *   - Registrations are checked by default; staff can uncheck anyone.
 *   - Rows without a phone number are auto-unchecked and disabled with a
 *     "No phone" chip so staff can see who they're skipping without opening
 *     the trainee list separately.
 *   - Message body supports `{{placeholder}}` substitution — the row of
 *     chips above the textarea inserts placeholders at the cursor.
 *     Backend interpolates per-trainee at send time.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Send, MessageSquare, Loader2, CheckSquare, Square, AlertCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_LEN = 1200;

// Keep in sync with `_TRAINING_PLACEHOLDER_FIELDS` in the backend
// training_register.py. Any change here should mirror there or the
// substitution will silently render as an empty string.
const FIELD_CHIPS = [
  { key: 'first_name', label: 'First name' },
  { key: 'name', label: 'Full name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'course_name', label: 'Course name' },
  { key: 'course_date', label: 'Course date' },
  { key: 'course_reference', label: 'Course ref' },
];

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

/** Local preview so the sender sees what the FIRST checked row will get. */
function renderPreview(body, ctx) {
  return String(body || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, k) => {
    const v = ctx[k];
    return v === undefined || v === null ? '' : String(v);
  });
}

export default function CourseSmsBlastModal({ open, onClose, courseId, courseSummary }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [course, setCourse] = useState(null);
  const [regs, setRegs] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [body, setBody] = useState('');
  const textareaRef = useRef(null);

  // Fetch the register once every time the modal opens. Cheap query
  // and lets staff always see the current list without a stale cache.
  useEffect(() => {
    if (!open || !courseId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/staff/training-courses/${courseId}`, {
          credentials: 'include', headers: authHeaders(),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!alive) return;
        setCourse(data.course || null);
        const rows = data.registrations || [];
        setRegs(rows);
        // Default: check every row that has a phone. No point pre-checking
        // rows we can't send to.
        const preChecked = new Set();
        rows.forEach((r) => { if ((r.phone || '').trim()) preChecked.add(r.id); });
        setChecked(preChecked);
      } catch (e) {
        toast.error('Failed to load register', { description: String(e.message || e).slice(0, 200) });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, courseId]);

  useEffect(() => {
    if (!open) {
      // Reset on close so the next open starts fresh.
      setBody('');
      setChecked(new Set());
      setRegs([]);
      setCourse(null);
    }
  }, [open]);

  const toggleOne = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const checkAllWithPhone = () => {
    setChecked(new Set(regs.filter((r) => (r.phone || '').trim()).map((r) => r.id)));
  };
  const uncheckAll = () => setChecked(new Set());

  const insertField = (key) => {
    const ta = textareaRef.current;
    const token = `{{${key}}}`;
    if (!ta) {
      setBody((b) => b + token);
      return;
    }
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // Restore focus + place cursor after the inserted token on next tick
    // so staff can keep typing without clicking back into the textarea.
    setTimeout(() => {
      ta.focus();
      const pos = start + token.length;
      try { ta.setSelectionRange(pos, pos); } catch (_e) { /* older browsers */ }
    }, 0);
  };

  const withPhoneCount = useMemo(() => regs.filter((r) => (r.phone || '').trim()).length, [regs]);
  const sendableIds = useMemo(
    () => regs.filter((r) => checked.has(r.id) && (r.phone || '').trim()).map((r) => r.id),
    [regs, checked],
  );

  const firstChecked = useMemo(
    () => regs.find((r) => sendableIds.includes(r.id)) || null,
    [regs, sendableIds],
  );
  const preview = useMemo(() => {
    if (!body.trim()) return '';
    if (!firstChecked) return body;
    const ctx = {
      name: firstChecked.name || '',
      first_name: (firstChecked.name || '').split(' ')[0] || '',
      email: firstChecked.email || '',
      phone: firstChecked.phone || '',
      course_name: course?.name || courseSummary?.name || '',
      course_date: course?.date || courseSummary?.date || '',
      course_reference: course?.reference || courseSummary?.reference || '',
    };
    return renderPreview(body, ctx);
  }, [body, firstChecked, course, courseSummary]);

  const overLen = body.length > MAX_LEN;
  const disabled = busy || loading || overLen || !body.trim() || sendableIds.length === 0;

  const submit = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/training-courses/${courseId}/sms-blast`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({
          body,
          registration_ids: sendableIds,
          channel: 'firetext_sms',
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      const parts = [];
      if (data.sent_count) parts.push(`${data.sent_count} sent`);
      if (data.failed_count) parts.push(`${data.failed_count} failed`);
      if (data.skipped_no_phone_count) parts.push(`${data.skipped_no_phone_count} no phone`);
      const msg = parts.join(' · ') || 'Nothing to send';
      if (data.failed_count > 0) toast.warning(`SMS blast: ${msg}`);
      else toast.success(`SMS blast: ${msg}`);
      onClose();
    } catch (e) {
      toast.error('SMS blast failed', { description: String(e.message || e).slice(0, 200) });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const courseName = course?.name || courseSummary?.name || '…';
  const courseDate = course?.date || courseSummary?.date || '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      data-testid="course-sms-blast-modal"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-gray-900 truncate" data-testid="course-sms-title">
                SMS the register
              </h2>
              <p className="text-xs text-gray-500 truncate">
                {courseName}{courseDate ? ` · ${courseDate}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => !busy && onClose()}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            data-testid="course-sms-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {/* Field-insert chips */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-gray-800">Message</label>
              <span
                className={`text-xs font-mono ${overLen ? 'text-red-600' : 'text-gray-500'}`}
                data-testid="course-sms-charcount"
              >
                {body.length}/{MAX_LEN}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="course-sms-fieldchips">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 self-center mr-1">
                Insert field:
              </span>
              {FIELD_CHIPS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => insertField(f.key)}
                  disabled={busy}
                  className="text-[11px] font-semibold border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-full px-2 py-0.5"
                  data-testid={`course-sms-field-${f.key}`}
                  title={`Inserts {{${f.key}}} at the cursor`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder={"Hi {{first_name}}, quick reminder your {{course_name}} training is on {{course_date}}. See you there."}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:border-blue-500/60"
              data-testid="course-sms-body"
              disabled={busy}
              maxLength={MAX_LEN + 200}
            />
            {preview && (
              <div className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <div className="font-semibold text-gray-500 mb-1 text-[10px] uppercase tracking-wider">
                  Preview (first checked)
                </div>
                <div className="whitespace-pre-wrap text-gray-800" data-testid="course-sms-preview">{preview}</div>
              </div>
            )}
          </div>

          {/* Recipient list */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-gray-800">
                Recipients
                <span className="ml-2 text-xs font-normal text-gray-500" data-testid="course-sms-selected-count">
                  {sendableIds.length} of {withPhoneCount} with a phone
                </span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={checkAllWithPhone}
                  className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                  data-testid="course-sms-check-all"
                  disabled={busy}
                >
                  Select all
                </button>
                <span className="text-gray-300">·</span>
                <button
                  type="button"
                  onClick={uncheckAll}
                  className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                  data-testid="course-sms-uncheck-all"
                  disabled={busy}
                >
                  None
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-6 text-sm text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Loading register…
              </div>
            ) : regs.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500">
                No-one on this register yet.
              </div>
            ) : (
              <ul
                className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto"
                data-testid="course-sms-recipients"
              >
                {regs.map((r) => {
                  const hasPhone = !!(r.phone || '').trim();
                  const isChecked = checked.has(r.id);
                  return (
                    <li
                      key={r.id}
                      className={`flex items-center gap-3 px-3 py-2 text-sm ${hasPhone ? 'hover:bg-blue-50/60 cursor-pointer' : 'bg-gray-50/60'}`}
                      onClick={() => hasPhone && !busy && toggleOne(r.id)}
                      data-testid={`course-sms-row-${r.id}`}
                    >
                      <span className="shrink-0" data-testid={`course-sms-check-${r.id}`}>
                        {isChecked ? (
                          <CheckSquare className={`w-4 h-4 ${hasPhone ? 'text-blue-600' : 'text-gray-300'}`} />
                        ) : (
                          <Square className={`w-4 h-4 ${hasPhone ? 'text-gray-400' : 'text-gray-300'}`} />
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{r.name || '—'}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {r.phone || <span className="text-amber-600 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> No phone</span>}
                          {r.email ? <span className="ml-2 text-gray-400">· {r.email}</span> : null}
                        </div>
                      </div>
                      {!hasPhone && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 shrink-0">
                          No phone
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2 bg-gray-50">
          <Button variant="outline" onClick={() => !busy && onClose()} disabled={busy} data-testid="course-sms-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={disabled}
            className="bg-blue-600 hover:bg-blue-500 text-white"
            data-testid="course-sms-send"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-1.5" />
                Send SMS to {sendableIds.length} trainee{sendableIds.length === 1 ? '' : 's'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
